import {
  AppBar,
  Box,
  Button,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { Transition } from "../../../../../../../../components";
import { useListaCEPService } from "../../../../../../../../../use-cases/geo-veles";
import { useAppSelector } from "../../../../../../../../hooks/store-hooks";
import { useEffect, useState } from "react";
import { FixedSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
// Icons
import FileIcon from "@mui/icons-material/FileUpload";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatCep } from "../../../../../../../../../utils";
import { ListaCEPImportFile } from "./ListaCEPImportFile";
import { EditCEP } from "./EditCEP";

export const EditListaCEPForm = (props: { openForm: boolean }) => {
  // State
  const { listaCEP } = useAppSelector((state) => state);
  // Use State
  const [addCepValue, setAddCepValue] = useState<string>("");
  const [cepSearchTerm, setCepSearchTerm] = useState<string>("");
  const [oldCEP, setOldCEP] = useState<string>("");

  // Novos estados para o diálogo de confirmação de exclusão
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
  const [cepToDelete, setCepToDelete] = useState<string>("");

  // Hook
  const {
    closeEditListaCEPForm,
    setEditListaCEPName,
    addItemToCEPList,
    resetListaCEPFileData,
    updateListaCEP,
    listaCEPRemoveCEP,
    openEditCEP,
    openImportFileListaCEP,
    setupListaCEP,
  } = useListaCEPService();

  const handleClickOpen = () => {
    openEditCEP(true);
  };

  useEffect(() => {
    setupListaCEP();
  }, [setupListaCEP]);

  // Função para abrir o diálogo de confirmação
  const handleOpenConfirmDelete = (cep: string) => {
    setCepToDelete(cep);
    setConfirmDeleteOpen(true);
  };

  // Função para confirmar a exclusão
  const handleConfirmDelete = () => {
    listaCEPRemoveCEP(cepToDelete);
    setConfirmDeleteOpen(false);
    setCepToDelete("");
  };

  // Função para cancelar a exclusão
  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setCepToDelete("");
  };

  return (
    <>
      {props.openForm === true && (
        <>
          <Dialog
            fullScreen
            open={listaCEP.openEditListaCEP}
            TransitionComponent={Transition}
            onClose={() => {
              closeEditListaCEPForm();
              setupListaCEP();
            }}
          >
            <AppBar sx={{ position: "relative" }}>
              <Toolbar>
                <Typography
                  sx={{ ml: 2, flex: 1 }}
                  textTransform="uppercase"
                  variant="h6"
                  component="div"
                >
                  Editar lista - {listaCEP.editListaCEPForm.fields.nome}
                </Typography>
                <Button
                  autoFocus
                  color="inherit"
                  onClick={() => {
                    closeEditListaCEPForm();
                    setupListaCEP();
                  }}
                >
                  Fechar
                </Button>
              </Toolbar>
            </AppBar>
            <Box p={3}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nome da lista"
                    placeholder="Digite o nome da lista"
                    defaultValue={listaCEP.editListaCEPForm.fields.nome}
                    onChange={debounce((e) => {
                      setEditListaCEPName(e.target.value);
                    }, 500)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Paper elevation={3}>
                    <Box p={3}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            textTransform="uppercase"
                            color="primary"
                          >
                            Adicionar CEP
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Digite o CEP que deseja adicionar"
                            value={addCepValue}
                            onChange={(e) => {
                              setAddCepValue(e.target.value);
                            }}
                            InputProps={{
                              endAdornment: (
                                <>
                                  <Button
                                    type="submit"
                                    onClick={() => {
                                      addItemToCEPList(addCepValue);
                                      requestAnimationFrame(() => {
                                        setAddCepValue("");
                                      });
                                    }}
                                  >
                                    Adicionar
                                  </Button>
                                </>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            textTransform="uppercase"
                            align="center"
                            color="primary"
                          >
                            OU
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <Button
                              startIcon={<FileIcon />}
                              variant="contained"
                              onClick={(e) => {
                                e.preventDefault();
                                openImportFileListaCEP(true);
                                resetListaCEPFileData();
                              }}
                            >
                              Importar arquivo
                            </Button>
                          </div>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Pesquisar CEP"
                    placeholder="Digite o CEP que deseja pesquisar"
                    fullWidth
                    value={cepSearchTerm}
                    onChange={(e) => {
                      setCepSearchTerm(e.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography color="primary" variant="h6">
                    Tamanho:{" "}
                    {listaCEP.editListaCEPForm.fields.ceps.length.toLocaleString(
                      "pt-BR"
                    )}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  height={
                    (listaCEP.editListaCEPForm.fields.ceps.length ?? 0) <= 3
                      ? 175
                      : 250
                  }
                  maxHeight={400}
                >
                  <AutoSizer>
                    {(data: any) => (
                      <FixedSizeList
                        height={
                          (listaCEP.editListaCEPForm.fields.ceps.length ?? 0) <=
                          3
                            ? 175
                            : 250
                        }
                        style={{
                          maxHeight: 400,
                        }}
                        width={data.width}
                        itemSize={46}
                        itemCount={
                          listaCEP.editListaCEPForm.fields.ceps.filter(
                            (cep) => {
                              if (
                                cepSearchTerm === "" ||
                                cepSearchTerm === undefined ||
                                cep.includes(cepSearchTerm)
                              ) {
                                return true;
                              } else {
                                return false;
                              }
                            }
                          ).length ?? 0
                        }
                        overscanCount={15}
                      >
                        {({ index, style }) => {
                          const item =
                            listaCEP.editListaCEPForm.fields.ceps.filter(
                              (cep) => {
                                if (
                                  cepSearchTerm === "" ||
                                  cepSearchTerm === undefined ||
                                  cep.includes(cepSearchTerm)
                                ) {
                                  return true;
                                } else {
                                  return false;
                                }
                              }
                            )[index];
                          return (
                            <ListItem
                              style={style}
                              secondaryAction={
                                <>
                                  <Button
                                    style={{
                                      marginRight: "10px",
                                    }}
                                    startIcon={<EditIcon />}
                                    onClick={() => {
                                      handleClickOpen();
                                      setOldCEP(item ?? "");
                                    }}
                                  >
                                    Editar
                                  </Button>
                                  <Button
                                    onClick={() => {
                                      handleOpenConfirmDelete(item);
                                    }}
                                    startIcon={<DeleteIcon />}
                                  >
                                    Excluir
                                  </Button>
                                </>
                              }
                            >
                              <ListItemText primary={formatCep(item)} />
                            </ListItem>
                          );
                        }}
                      </FixedSizeList>
                    )}
                  </AutoSizer>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation();
                      updateListaCEP();
                    }}
                    variant="contained"
                  >
                    Salvar alterações
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Dialog>

          {/* Dialog de confirmação de exclusão */}
          <Dialog
            open={confirmDeleteOpen}
            onClose={handleCancelDelete}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {"Confirmar exclusão"}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Tem certeza que deseja excluir o CEP{" "}
                <strong>{formatCep(cepToDelete)}</strong>?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancelDelete} color="primary">
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmDelete}
                color="error"
                autoFocus
                variant="contained"
              >
                Excluir
              </Button>
            </DialogActions>
          </Dialog>

          <ListaCEPImportFile
            openImportFile={listaCEP.openImportFileListaCEP}
          />
          <EditCEP openEditCEP={listaCEP.openEditCEP} oldCEP={oldCEP} />
        </>
      )}
    </>
  );
};